/**
 * Small image-processing kernels shared by the palm pipeline
 *
 * Everything works on plain objects — `{ width, height, data }` — never on DOM
 * classes, so the whole pipeline runs unchanged in a Web Worker, on the main
 * thread and under Node in the test suite. A "gray" image is `data:
 * Float32Array` in [0, 1]; an RGBA image is `data: Uint8ClampedArray` exactly as
 * `ImageData` provides.
 *
 * No function mutates its input.
 *
 * © Manish Jagdish Thatte
 */

export function makeGray(width, height, fill = 0) {
  const data = new Float32Array(width * height);
  if (fill) data.fill(fill);
  return { width, height, data };
}

export function cloneGray(g) {
  return { width: g.width, height: g.height, data: Float32Array.from(g.data) };
}

/** A 1-D Gaussian (order 0), its first (order 1) or second (order 2) derivative. */
export function gaussKernel(sigma, order = 0) {
  const r = Math.max(1, Math.ceil(sigma * 3.5));
  const k = new Float32Array(2 * r + 1);
  const s2 = sigma * sigma;
  let sum = 0;
  for (let i = -r; i <= r; i++) {
    const g = Math.exp(-(i * i) / (2 * s2));
    let v = g;
    if (order === 1) v = (-i / s2) * g;
    else if (order === 2) v = ((i * i - s2) / (s2 * s2)) * g;
    k[i + r] = v;
    sum += g;
  }
  // Every order shares the zeroth-order normalisation: the derivative of a
  // unit-area Gaussian, not of an unnormalised one.
  for (let i = 0; i < k.length; i++) k[i] /= sum;
  if (order === 2) {
    // Zero-mean, so a flat region has exactly zero second derivative.
    let m = 0; for (const v of k) m += v; m /= k.length;
    for (let i = 0; i < k.length; i++) k[i] -= m;
  }
  return k;
}

/** Separable convolution with edge replication. `kx` along x, `ky` along y. */
export function convolveSep(g, kx, ky) {
  const { width: w, height: h, data } = g;
  const rx = (kx.length - 1) >> 1; const ry = (ky.length - 1) >> 1;
  const tmp = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let i = -rx; i <= rx; i++) {
        const xx = x + i < 0 ? 0 : x + i >= w ? w - 1 : x + i;
        acc += data[row + xx] * kx[i + rx];
      }
      tmp[row + x] = acc;
    }
  }
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let i = -ry; i <= ry; i++) {
        const yy = y + i < 0 ? 0 : y + i >= h ? h - 1 : y + i;
        acc += tmp[yy * w + x] * ky[i + ry];
      }
      out[y * w + x] = acc;
    }
  }
  return { width: w, height: h, data: out };
}

export function gaussianBlur(g, sigma) {
  const k = gaussKernel(sigma, 0);
  return convolveSep(g, k, k);
}

/** Bilinear resize of a gray image. */
export function resizeGray(g, width, height) {
  if (width === g.width && height === g.height) return cloneGray(g);
  const out = makeGray(width, height);
  const sx = g.width / width; const sy = g.height / height;
  for (let y = 0; y < height; y++) {
    const fy = Math.min(g.height - 1, Math.max(0, (y + 0.5) * sy - 0.5));
    const y0 = Math.floor(fy); const y1 = Math.min(g.height - 1, y0 + 1); const ty = fy - y0;
    for (let x = 0; x < width; x++) {
      const fx = Math.min(g.width - 1, Math.max(0, (x + 0.5) * sx - 0.5));
      const x0 = Math.floor(fx); const x1 = Math.min(g.width - 1, x0 + 1); const tx = fx - x0;
      const a = g.data[y0 * g.width + x0]; const b = g.data[y0 * g.width + x1];
      const c = g.data[y1 * g.width + x0]; const d = g.data[y1 * g.width + x1];
      out.data[y * width + x] = (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
    }
  }
  return out;
}

/** Mean and standard deviation. */
export function stats(data) {
  let s = 0; for (let i = 0; i < data.length; i++) s += data[i];
  const mean = s / (data.length || 1);
  let v = 0; for (let i = 0; i < data.length; i++) v += (data[i] - mean) ** 2;
  return { mean, std: Math.sqrt(v / (data.length || 1)) };
}

export function maxOf(data) {
  let m = -Infinity; for (let i = 0; i < data.length; i++) if (data[i] > m) m = data[i];
  return m;
}

/** A binary image: `data: Uint8Array` of 0/1. */
export function makeBinary(width, height) {
  return { width, height, data: new Uint8Array(width * height) };
}

/** 8-connected component labelling. Returns `{ labels: Int32Array, sizes: number[] }` (label 0 = background). */
export function label8(bin) {
  const { width: w, height: h, data } = bin;
  const labels = new Int32Array(w * h);
  const sizes = [0];
  let next = 1;
  const stack = [];
  for (let i = 0; i < w * h; i++) {
    if (!data[i] || labels[i]) continue;
    let size = 0;
    stack.push(i); labels[i] = next;
    while (stack.length) {
      const p = stack.pop(); size++;
      const px = p % w; const py = (p - px) / w;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = py + dy; if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = px + dx; if (xx < 0 || xx >= w || (!dx && !dy)) continue;
          const q = yy * w + xx;
          if (data[q] && !labels[q]) { labels[q] = next; stack.push(q); }
        }
      }
    }
    sizes.push(size); next++;
  }
  return { labels, sizes };
}
